import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { COMMA, ENTER } from '@angular/cdk/keycodes'
import { MatChipInputEvent } from '@angular/material/chips'
import { cloneDeep } from 'lodash'

import { CHIP_TYPE, MAX_ASSET_TYPE, MIN_ASSET_TYPE } from 'src/app/constants/internationalized-constants-en'

@Component({
  selector: 'app-chips',
  templateUrl: './chips.component.html',
  styleUrls: ['./chips.component.scss']
})
export class ChipsComponent {

  @Input() chipTitle: string = 'Title'
  @Input() placeholder: string = 'Placeholder'
  @Input() maxChips: number = MAX_ASSET_TYPE
  @Input() minChips: number = MIN_ASSET_TYPE
  @Input() chipType: CHIP_TYPE = CHIP_TYPE.STRING
  @Input() chipList: string[] | number[] = []
  // angular escape work not match with this regex so change to regex string to fix it
  /* eslint-disable */
  @Input() regex: string | RegExp = "^[a-zA-Z0-9_ %&+=:,.\\/()\\[\\]|-]*$"
  @Input() chipsStatus: boolean = true
  @Input() maxLengthNumber: number = 2
  @Input() disabledChipList: boolean = false
  @Output() valueChange = new EventEmitter()
  @Output() inputStatusChange = new EventEmitter()

  @ViewChild('chipString', { read: ElementRef }) chipString?: ElementRef<HTMLInputElement>
  @ViewChild('chipNumber', { read: ElementRef }) chipNumber?: ElementRef<HTMLInputElement>
  @ViewChild('matChipList', { read: ElementRef }) matChipList?: ElementRef<HTMLElement>

  CHIP_TYPE = CHIP_TYPE
  visible = true
  selectable = true
  removable = true
  addOnBlur = true
  inputChipValid = true
  chipInputValue = ''
  private pendingKeyboardRemoveIndex: number | null = null

  readonly separatorKeysCodes: number[] = [COMMA, ENTER]

  constructor() { }

  focusChipInput() {
    // When tabbing into the chip-list, Angular Material focuses the list container.
    // Move focus into the actual input so users can type/add chips.
    const inputEl = this.chipType === CHIP_TYPE.NUMBER
      ? this.chipNumber?.nativeElement
      : this.chipString?.nativeElement

    if (inputEl) {
      setTimeout(() => inputEl.focus(), 0)
      return
    }

    // If input is not present (e.g. max chips reached), keep it keyboard-usable
    // by focusing the first remove button.
    setTimeout(() => {
      const firstRemoveBtn = this.matChipList?.nativeElement?.querySelector('button[matChipRemove]') as HTMLElement | null
      firstRemoveBtn?.focus()
    }, 0)
  }

  add(event: MatChipInputEvent): void {
    const input = event.input
    const value = event.value

    if (input && input.validity && !input.validity.valid) {
      return
    }
    const list = cloneDeep(this.chipList)
    if (this.chipType === CHIP_TYPE.STRING && (value || '').trim()) {
      // Fix bug ECT2-233 
      if ((list as string[]).includes(value.trim())) {
        return
      } else {
        (list as string[]).push(value.trim())
      }
    } else if (this.chipType === CHIP_TYPE.NUMBER && value) {
      (list as number[]).push(Number(value))
    }
    const reachedMaxChips = Boolean(this.maxChips) && list.length >= this.maxChips
    this.valueChange.emit(list)

    if (input) {
      this.chipInputValue = ''
      input.value = ''
    }

    if (reachedMaxChips) {
      setTimeout(() => {
        this.focusFirstRemoveButton()
      }, 0)
    }
  }

  remove(index: number): void {
    const value = cloneDeep(this.chipList)
    if (index >= 0) {
      const shouldRestoreFocus = this.pendingKeyboardRemoveIndex === index
      value.splice(index, 1)
      this.valueChange.emit(value)
      if (shouldRestoreFocus) {
        this.focusAfterKeyboardRemove(index)
      }
    }
    this.pendingKeyboardRemoveIndex = null
  }

  onRemoveButtonKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Tab') {
      const isFirstChip = index === 0
      const isLastChip = index === this.chipList.length - 1
      const hasInput = Boolean(this.getInputElement())

      // If input is available, keep natural backward tab order inside chips:
      // first remove chip -> input.
      if (event.shiftKey && isFirstChip && hasInput) {
        return
      }

      if ((event.shiftKey && isFirstChip) || (!event.shiftKey && isLastChip)) {
        event.preventDefault()
        const keyTarget = (event.currentTarget as HTMLElement | null) || (event.target as HTMLElement | null)
        this.focusAdjacentField(keyTarget, event.shiftKey)
      }
      return
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      this.pendingKeyboardRemoveIndex = index

      if (event.key === ' ' || event.key === 'Spacebar') {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        this.remove(index)
      }
    }
  }

  onChipInputKeydown(event: KeyboardEvent, chip: HTMLInputElement): void {
    this.handleStatusChange(chip)

    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault()
      const keyTarget = (event.currentTarget as HTMLElement | null) || (event.target as HTMLElement | null)
      this.focusAdjacentField(keyTarget, true)
    }
  }

  handleStatusChange(chip: HTMLInputElement) {
    setTimeout(() => {
      const status = chip && chip.validity && chip.validity.valid
      this.chipInputValue = chip.value
      this.inputChipValid = status
      this.inputStatusChange.emit(status)
    }, 100)
  }

  private focusAfterKeyboardRemove(removedIndex: number): void {
    setTimeout(() => {
      const removeButtons = this.getRemoveButtons()
      const nextButton = removeButtons[removedIndex]
      if (nextButton) {
        nextButton.focus()
        return
      }

      const previousButton = removeButtons[removedIndex - 1]
      if (previousButton) {
        previousButton.focus()
        return
      }

      this.focusInputElement()
    }, 0)
  }

  private getRemoveButtons(): HTMLButtonElement[] {
    const nodeList = this.matChipList?.nativeElement?.querySelectorAll('button[matChipRemove]')
    return nodeList ? Array.from(nodeList) as HTMLButtonElement[] : []
  }

  private focusInputElement(): void {
    const inputEl = this.getInputElement()

    if (inputEl) {
      inputEl.focus()
      return
    }

    this.focusFirstRemoveButton()
  }

  private focusFirstRemoveButton(): void {
    const firstRemoveBtn = this.getRemoveButtons()[0]
    firstRemoveBtn?.focus()
  }

  private getInputElement(): HTMLInputElement | undefined {
    return this.chipType === CHIP_TYPE.NUMBER
      ? this.chipNumber?.nativeElement
      : this.chipString?.nativeElement
  }

  private focusAdjacentField(currentTarget: HTMLElement | null, backward: boolean): void {
    if (!currentTarget) {
      return
    }

    const focusableElements = this.getFocusableElementsInScope()
    const currentChipContainer = this.getCurrentChipContainer()
    const currentIndex = this.getNavigationStartIndex(focusableElements, currentTarget, currentChipContainer, backward)
    if (currentIndex < 0) {
      return
    }

    const step = backward ? -1 : 1
    let targetIndex = currentIndex + step
    while (targetIndex >= 0 && targetIndex < focusableElements.length) {
      const candidate = focusableElements[targetIndex]
      if (!currentChipContainer || !this.isInsideCurrentChipContainer(currentChipContainer, candidate)) {
        candidate.focus()
        return
      }
      targetIndex += step
    }
  }

  private getNavigationStartIndex(
    focusableElements: HTMLElement[],
    currentTarget: HTMLElement,
    currentChipContainer: HTMLElement | null,
    backward: boolean
  ): number {
    const currentIndex = focusableElements.indexOf(currentTarget)
    if (currentIndex >= 0) {
      return currentIndex
    }

    if (!currentChipContainer) {
      return -1
    }

    const internalIndexes = focusableElements
      .map((el, index) => this.isInsideCurrentChipContainer(currentChipContainer, el) ? index : -1)
      .filter((index) => index >= 0)

    if (!internalIndexes.length) {
      return -1
    }

    return backward
      ? Math.min(...internalIndexes)
      : Math.max(...internalIndexes)
  }

  private getCurrentChipContainer(): HTMLElement | null {
    const chipListEl = this.matChipList?.nativeElement
    if (!chipListEl) {
      return null
    }

    return chipListEl.closest('mat-form-field') as HTMLElement | null || chipListEl
  }

  private isInsideCurrentChipContainer(container: HTMLElement, candidate: HTMLElement): boolean {
    try {
      return container.contains(candidate)
    } catch {
      return false
    }
  }

  private getFocusableElementsInScope(): HTMLElement[] {
    const chipListEl = this.matChipList?.nativeElement
    if (!chipListEl) {
      return []
    }

    const scope = chipListEl.closest('mat-dialog-container, [role="dialog"]') as ParentNode | null
      || chipListEl.ownerDocument

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')

    const elements = Array.from(scope.querySelectorAll(focusableSelector)) as HTMLElement[]
    return elements.filter((el) => this.isElementFocusable(el))
  }

  private isElementFocusable(element: HTMLElement): boolean {
    if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') {
      return false
    }

    const view = element.ownerDocument?.defaultView
    const style = view ? view.getComputedStyle(element) : null
    if (style && (style.display === 'none' || style.visibility === 'hidden')) {
      return false
    }

    return element.tabIndex >= 0 && (element.offsetParent !== null || style?.position === 'fixed')
  }
}

